import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../components/Ui/LoadingOverlay";
import Pagination from "../../../components/Ui/Paging";
import { calculatePage } from "../../../utils/paging";
import { adminGetSubscribers, adminDeleteSubscriber } from "../../../services/statusAdmin";
import { formatDateToCustomString } from "../../../utils/utils";

const STATUS_COLORS = {
  active: "bg-green-100 text-green-800",
  pending: "bg-yellow-100 text-yellow-800",
  unsubscribed: "bg-gray-100 text-gray-500",
};

export default function AdminStatusSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paging, setPaging] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [deleting, setDeleting] = useState(null);

  const fetch = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await adminGetSubscribers({ page, limit: paging.limit });
      setSubscribers(res.data.data || []);
      setPaging((p) => ({
        ...p,
        page,
        total: res.data.total,
        totalPages: calculatePage(res.data.total, p.limit),
      }));
    } catch {
      toast.error("Failed to load subscribers");
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging.limit]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this subscriber?")) return;
    setDeleting(id);
    try {
      await adminDeleteSubscriber(id);
      toast.success("Deleted");
      setSubscribers((prev) => prev.filter((s) => s._id !== id));
      setPaging((p) => ({ ...p, total: p.total - 1 }));
    } catch {
      toast.error("Failed to delete");
    }
    setDeleting(null);
  };

  return (
    <div className="relative">
      <div className="flex w-full justify-between items-center p-3 flex-wrap">
        <h2 className="font-semibold text-3xl text-gray-900">
          Status Subscribers
          <span className="ml-3 text-base font-normal text-gray-400">({paging.total} total)</span>
        </h2>
      </div>

      <div className="overflow-x-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th className="px-6 py-3">#</th>
              <th className="px-6 py-3">Email</th>
              <th className="px-6 py-3">Status</th>
              <th className="px-6 py-3">Confirmed At</th>
              <th className="px-6 py-3">Subscribed At</th>
              <th className="px-6 py-3">Unsubscribed At</th>
              <th className="px-6 py-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {subscribers.map((item, i) => (
              <tr key={item._id} className="bg-white border-b hover:bg-gray-50">
                <td className="px-6 py-4">{(paging.page - 1) * paging.limit + i + 1}</td>
                <td className="px-6 py-4 font-medium text-gray-900">{item.email}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[item.status] || "bg-gray-100 text-gray-500"}`}>
                    {item.status}
                  </span>
                </td>
                <td className="px-6 py-4">{item.confirmedAt ? formatDateToCustomString(item.confirmedAt) : "—"}</td>
                <td className="px-6 py-4">{formatDateToCustomString(item.createdAt)}</td>
                <td className="px-6 py-4">{item.unsubscribedAt ? formatDateToCustomString(item.unsubscribedAt) : "—"}</td>
                <td className="px-6 py-4 text-center">
                  <button
                    onClick={() => handleDelete(item._id)}
                    disabled={deleting === item._id}
                    className="text-red-500 hover:text-red-700 disabled:opacity-50"
                    title="Delete subscriber"
                  >
                    {deleting === item._id ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="animate-spin">
                        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                        <path d="M10 11v6" />
                        <path d="M14 11v6" />
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                      </svg>
                    )}
                  </button>
                </td>
              </tr>
            ))}
            {subscribers.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400">No subscribers yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {paging.totalPages > 1 && (
        <Pagination
          totalPages={paging.totalPages}
          currentPage={paging.page}
          onPageChange={fetch}
        />
      )}
      {loading && <LoadingOverlay />}
    </div>
  );
}
